import axios from "axios";
import { axiosClient } from "../App";
import { getCookie } from "../utils/Common";

export const getAutocompleteAddress = (apiKey: string, searchValue: string) =>
  axiosClient.get(
    `https://us-autocomplete-pro.api.smartystreets.com/lookup?key=${apiKey}&search=${searchValue}`
  );

export const getZipCodeList = (apiKey: string, searchValue: string) =>
  axiosClient.get(
    `https://us-zipcode.api.smartystreets.com/lookup?auth-id=${apiKey}&zipcode=${searchValue}`
  );

export const getToken = (body: any, headers: any, clientConfig: any) =>
  axios.post(`${clientConfig?.ViteAppRealms}/token`, body, { headers });

export const logoutApp = (url: any, formBodyString: any, headers: any) =>
  axiosClient.post(url, formBodyString, { headers });

export const loanDataBasedOnLoanId = (body: any) =>
  axiosClient.post(`/online-sizer/fetch`, body);

// Triggers the escalation email (Brevo template) for a loan. Fire-and-forget
// from the caller's perspective — a mail failure must not fail the escalation.
export const sendEscalationNotification = (payload: {
  loanId: string;
  orgId?: string;
  evaluationId?: string | number;
  action?: "ESCALATED" | "APPROVED" | "REJECTED";
  escalatedBy?: string;
  actionBy?: string;
  actionByRole?: string;
  escalatedTo?: string | string[];
  reason?: string;
  propertyAddress?: string;
  loanAmount?: string | number;
  interestRate?: string | number;
}) =>
  axiosClient.post(`/online-sizer/escalation/notify`, payload, {
    // The service reads the org from the org-id header.
    headers: payload.orgId ? { "org-id": payload.orgId } : undefined,
  });

export const createLoanInLos = (createPayload: any, realmHeaders: any) =>
  axiosClient.post(`/public-api/bff/loans/submit-to-los`, createPayload, {
    headers: realmHeaders,
  });

export const patchSaveApplication = (
  loanId: any,
  tempData: any,
  preservePricingValidity?: boolean,
) => {
  let dataToSave = tempData;
  if (!preservePricingValidity && tempData?.loanEconomics?.isPricingValid) {
    dataToSave = {
      ...tempData,
      loanEconomics: {
        ...tempData.loanEconomics,
        isPricingValid: false,
      },
    };
  }
  return axiosClient.patch(`/online-sizer/update`, [
    {
      loanId,
      data: dataToSave,
    },
  ]);
};

// External audit microservice — uses the same Bearer/kc-realm/partyid auth
// as every other call here.

export type AuditResource = { resourceType: string; resourceId: string };
export type AuditBulkEntry = {
  resourceId: string;
  resourceType: string;
  data: { field: string; value: any; comment: string; updatedBy: string }[];
};

// Which fields (across one or more resources) already have audit history.
export const fetchAuditedFields = (
  resources: AuditResource[],
  realmHeaders?: any,
) =>
  axiosClient.post(
    `/public-api/audit/fields/fetch`,
    { resources },
    { headers: realmHeaders },
  );

// Last `limit` audit entries for one specific field.
export const fetchFieldAuditHistory = (
  resourceType: string,
  resourceId: string,
  field: string,
  limit = 3,
  offset = 0,
  realmHeaders?: any,
) =>
  axiosClient.get(
    `/public-api/audit/${resourceType}/${resourceId}/${encodeURIComponent(field)}?limit=${limit}&offset=${offset}`,
    { headers: realmHeaders },
  );

// Records edits to already-audited fields (every save after the first).
export const postAuditBulk = (entries: AuditBulkEntry[], realmHeaders?: any) =>
  axiosClient.post(`/public-api/audit/bulk`, entries, {
    headers: realmHeaders,
  });

// Seeds audit history for every field at once — fired once, on a loan's
// very first save, with the whole loan record flattened to dot/bracket paths.
export const postInitialAudit = (
  loanId: string,
  flattenedData: Record<string, any>,
  realmHeaders?: any,
) =>
  axiosClient.post(
    `/public-api/audit/ONLINE_SIZER/${loanId}`,
    flattenedData,
    { headers: realmHeaders },
  );

export const postCreateLoan = (createPayload: any, realmHeaders: any) =>
  axiosClient.post(`/public-api/aggregate/loans/create`, createPayload, {
    headers: realmHeaders,
  });

export const postCalculateEligibility = (reqBody: any, realmHeaders: any) =>
  axiosClient.post(
    `/public-api/template/fetch?locale=NA&countryCode=NA&partialProcess=false`,
    reqBody,
    {
      headers: realmHeaders,
    }
  );

export const getClientResponseConfig = (url: any, body: any) =>
  axiosClient.get(url, body);

export const clickContinue = (reqBody: any) =>
  axiosClient.post(`/online-sizer/create`, reqBody);

export const postRowData = (body: any) =>
  axiosClient.post(`/online-sizer/fetch/query`, body);

export const customerData = (decodedEmail: any) =>
  axiosClient.get(`/public-api/customer/${decodedEmail}`);

export const orgBucketResponse = (bucketPartyId: any) =>
  axiosClient.get(
    `/public-api/aggregate/accountmap/originator/${bucketPartyId}`
  );

export const filterDataResponse = (
  filters: any,
  property?: string,
  appendPrefix?: boolean
) =>
  axiosClient.post(`/online-sizer/aggregate/query`, {
    propertyPath: appendPrefix ? `data.${property}` : property,
    conditions: [filters],
  });

export const getTemplateResponseDSCR = (
  tpId: any,
  orgId: string,
  reqBody: any
) => {
  let url = `/public-api/template/fetch?locale=NA&countryCode=NA&partialProcess=true&tpId=${tpId}`;
  if (orgId) {
    url = `${url}&partyId=${orgId}`;
  }
  return axiosClient.post(url, reqBody);
};

export const getTemplateResponse = (
  partyId: any,
  reqBody: any,
  publicAPI2Headers: any
) =>
  axiosClient.post(
    `/public-api/template/fetch?locale=NA&countryCode=NA&partialProcess=true&partyId=${partyId}`,
    reqBody,
    {
      headers: publicAPI2Headers,
    }
  );

export const getGuidlineEvaluationResult = (loanType: any, reqBody: any) => {
  const urlEndPoint =
    loanType === "InvestorDSCR"
      ? "public-api/aggregate/online-sizer/evaluation/v1.0"
      : "public-api/ruleevaluation/v1.0";
  return axiosClient.post(`/${urlEndPoint}`, reqBody);
};

export const getEligibleResult = (
  eligiblePayload: any,
  publicAPI2Headers: any
) =>
  axiosClient.post(
    `/public-api/aggregate/takeout-partner/change-takeout-partners?skipSave=true`,
    eligiblePayload,
    {
      headers: publicAPI2Headers,
    }
  );

export const postUpdateLoanUrl = (updatePayload: any, realmHeaders: any) =>
  axiosClient.post(`/public-api/aggregate/loans`, updatePayload, {
    headers: realmHeaders,
  });

export const postUpdatePropertyUrl = (
  createResultLoanId: any,
  propertyPayload: any,
  realmHeaders: any
) =>
  axiosClient.post(
    `/public-api/aggregate/loan_property/${createResultLoanId}/properties?stage=FES`,
    propertyPayload,
    {
      headers: realmHeaders,
    }
  );

export const postDownloadCondo = (requestObject: any) =>
  axiosClient.post(`/public-api/aggregate/loans/condo`, requestObject);

export const postForceLogout = (
  formBody: any,
  headers: any,
  clientConfig: any
) =>
  axiosClient.post(`${clientConfig.ViteAppRealms}/logout`, formBody, {
    headers,
  });

export const postFetchRefreshToken = (
  formBody: any,
  headers: any,
  clientConfig: any
) =>
  axiosClient.post(`${clientConfig.ViteAppRealms}/token`, formBody, {
    headers,
  });

export const fetchTemplateLabel = (templateKeys: any, clientId: string) => {
  let url = `/public-api/template/fetch?locale=NA&countryCode=NA&partialProcess=true`;
  if (clientId) {
    url += `&partyId=${clientId}`;
  }
  return axiosClient.post(url, templateKeys);
};

export const fetchPartnerMap = () => {
  const url = `/public-api/credit-line/fetch?creditLineType=TAKEOUT_PARTNER`;
  return axiosClient.get(url);
};

export const getCriticalTests = (rVersion: string) => {
  const url = `/public-api/ruleevaluation/ruleattributes?ruleType=30year&ruleVersion=${rVersion}`;
  const body = ["criticalTestLoanEligibility"];
  return axiosClient.post(url, body);
};

// Given the guideline-API attribute names a user just edited, tells whether
// any of them feeds the rules (waivers / test failures). Body is the plain
// array of edited attribute keys.
export const getRuleAttributeImpact = (
  attributes: string[],
  finalLoanType: string,
) => axiosClient.post(`/public-api/ruleevaluation/attribute/${finalLoanType}`, attributes);

export const evaluatePartnerGuidelines = (
  reqBody: any,
  generateBuyDownOptions: boolean = true
) => {
  const url = `/public-api/aggregate/online-sizer/dscr/evaluation/partner?generateBuyDownOptions=${generateBuyDownOptions}`;
  return axiosClient.post(url, reqBody);
};

export const convertLoanSizingData = (reqBody: any) => {
  const url = `/online-sizer/convert`;
  return axiosClient.post(url, reqBody);
};

export const uploadWaiverRequestDoc = (url: string, reqBody:any)=>{
  return axiosClient.post(url, reqBody);
};

export const fetchWaiverRequestDoc = (url: string, reqBody:any)=>{
  return axiosClient.post(url, reqBody);
}

export const uploadFileToCloud = (file: FormData, url: string) => {
  return axiosClient.put(url, file);
}

export const getBorrowerDetailsBasedOnEmail = (payload: any) => {
  const url = `/public-api/bff/borrower/check-borrower-email`;
  return axiosClient.post(url, payload);
}

export const getGuarantorBasedOnBorrowerId = (borrowerId: string, baseUrl: string) => {
  const url = `${baseUrl}/bff/api/v1/borrowers/${borrowerId}/guarrantors`;
  return axiosClient.get(url);
}

// secondaryIdentifierName/Value ride along as query params here too (not
// just in reqBody) — mirrors how fetchWaiversInCommonService/tc-bff-service's
// GET already expects them on the querystring for this same LOAN_STAGE/FES
// identifier.
export const createWaiversInCommonService = (reqBody: any, id: string) => {
  const url = `/waiverservice/LOAN/${id}/waiver`;
  return axiosClient.post(url, reqBody, {
    params: {
      secondaryIdentifierName: reqBody?.secondaryIdentifierName,
      secondaryIdentifierValue: reqBody?.secondaryIdentifierValue,
    },
  });
};

export const updateWaiversInCommonService = (reqBody: any, waiverId: string) => {
  const url = `/waiverservice/waiver/${waiverId}/response`;
  return axiosClient.post(url, reqBody);
}

// Soft delete — DELETE /waiverservice/waiver/:waiverId (waiver service API
// §1.7). Used when re-requesting a pricing exception: the prior ask is
// deleted outright rather than appended to as a new response.
export const deleteWaiverInCommonService = (waiverId: string) => {
  const url = `/waiverservice/waiver/${waiverId}`;
  return axiosClient.delete(url);
}

export const fetchWaiversInCommonService = (id: string, sizerLoanId: string) => {
  const url = `/waiverservice/LOAN/${id}/waiver`;
  return axiosClient.get(url, { params: { sizerLoanId } });
};

// Tells the waiver service a loan/property data change just happened, so it
// can decide (server-side) whether any currently-decided waiver was approved
// against numbers that just moved and, if so, revert it back to Requested.
// Only the changed fields are ever sent — never the whole loan — see
// buildWaiverUpdateRequestFromAuditEdits in components/waivers/utils.ts.
export const updateApprovedWaiversInCommonService = (
  loanId: string,
  reqBody: {
    loanStage: string;
    loanSizerType?: string | null;
    loanType: string;
    updateRequest: {
      loanData: Record<string, any>;
      propertyData: Record<string, any>[];
    };
  },
) => {
  const url = `/waiverservice/LOAN/${loanId}/waiver/update-approved-waivers`;
  return axiosClient.post(url, reqBody);
};

// Resolves a batch of Keycloak userIds (e.g. waivers' createdBy/responseBy)
// to their profiles in one call. Proxied through our own backend rather than
// calling Keycloak's admin API directly — that admin endpoint requires a
// privileged service-account token, which must never be held client-side.
// Response is an array of profile objects, each identified by its own `id`.
export const getUserDetails = (userIds: string[]) =>
  axiosClient.post(`/online-sizer/users`, { userIds });

// Links the sizer-side loanId (used throughout the waiver service) to the
// loanId assigned by whichever platform the loan was just submitted to.
// destinationPlatformIdName is caller-supplied because it's tenant-specific
// (see mapWaiverLoanIdToDestination in components/waivers/utils.ts).
export const mapWaiverLoanId = (
  sizerLoanId: string,
  destinationPlatformIdName: string,
  destinationLoanId: string,
) => {
  const url = `/waiverservice/waiver/loan-id/map`;
  return axiosClient.post(url, {
    sourcePlatformIdName: "sizerLoanId",
    sourceLoanId: sizerLoanId,
    destinationPlatformIdName,
    destinationLoanId,
  });
};

export const getWaiverDocumentUploadUrl = (waiverId: string, fileName: string) => {
  const url = `/waiverservice/waiver/${waiverId}/document/upload-url`;
  return axiosClient.post(url, { fileName });
};

// GCS's presigned URL isn't signed with a Content-Type, so sending one (even
// the browser's auto-detected Blob type) causes a signature mismatch — the
// header must be explicitly cleared rather than omitted.
export const uploadWaiverDocumentToGcs = (file: File, uploadUrl: string) =>
  axiosClient.put(uploadUrl, file, { headers: { "Content-Type": "" } });

export const attachWaiverDocumentPaths = (waiverId: string, paths: string[]) => {
  const url = `/waiverservice/waiver/${waiverId}/document`;
  return axiosClient.post(url, { paths: paths });
};

// This URL's "download-url" segment matches useAxios.ts's exceptionUrls
// substring check on "download" (there for the unrelated external
// merchantsmtg.info file-download host), which makes the interceptor skip
// BOTH Authorization and kc-realm for this call. Rather than touch that
// shared, broadly-used interceptor — other flows may depend on its current
// behavior in ways not fully auditable from here — both headers are set
// explicitly on this one call so it never depends on the interceptor either
// way, on the first attempt or any retry.
export const getWaiverDocumentDownloadUrl = (
  waiverId: string,
  path: string,
  realmHeaders?: any,
) => {
  const url = `/waiverservice/waiver/${waiverId}/document/download-url`;
  const accessToken = getCookie("sizer_access_token") ?? "";
  return axiosClient.post(
    url,
    { path },
    {
      headers: {
        ...realmHeaders,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    },
  );
};

// chatLineOrderId is assigned by the service (max existing + 1 for this
// waiver's chat) — never sent from the client.
export const addWaiverChatLine = (
  waiverId: string,
  reqBody: {
    line: string;
    hasDocument?: boolean;
    documentPaths?: string[];
    tags?: string[];
    type?: string;
  },
) => {
  const url = `/waiverservice/waiver/${waiverId}/chat/line`;
  return axiosClient.post(url, reqBody);
};

export const getWaiverChatDocumentUploadUrl = (waiverId: string, fileName: string) => {
  const url = `/waiverservice/waiver/${waiverId}/chat/document/upload-url`;
  return axiosClient.post(url, { fileName });
};

// Same "download" exceptionUrls gotcha as getWaiverDocumentDownloadUrl above
// (this URL also contains "download-url") — both headers are set explicitly
// here rather than relying on the shared interceptor.
export const getWaiverChatDocumentDownloadUrl = (
  waiverId: string,
  paths: string[],
  realmHeaders?: any,
) => {
  const url = `/waiverservice/waiver/${waiverId}/chat/document/download-url`;
  const accessToken = getCookie("sizer_access_token") ?? "";
  return axiosClient.post(
    url,
    { paths },
    {
      headers: {
        ...realmHeaders,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    },
  );
};

// Upserts last_read_on for the current user on this waiver's chat — not
// scoped under /waiver/{waiverId}/... like the other chat endpoints, the
// waiverId is passed in the body instead.
export const markWaiverChatRead = (waiverId: string) => {
  const url = `/waiverservice/chats/mark-read`;
  return axiosClient.post(url, { waiverId });
};